import { createCalendar, updateCalendar } from "@/api/calendars.api";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import type { Calendar } from "@terrabase/db";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

const calendarFormSchema = z.object({
  name: z
    .string()
    .min(1, "Calendar name is required")
    .max(255, "Calendar name is too long"),
});

type CalendarFormData = z.infer<typeof calendarFormSchema>;

interface CalendarFormProps {
  calendar?: Calendar;
  isEdit?: boolean;
  onSuccess?: () => void;
  showCard?: boolean;
}

export function CalendarForm({
  calendar,
  isEdit = false,
  onSuccess,
  showCard = true,
}: CalendarFormProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const form = useForm<CalendarFormData>({
    resolver: zodResolver(calendarFormSchema),
    defaultValues: {
      name: calendar?.name || "",
    },
  });

  const { mutate: createCalendarMutation, isPending: isCreating } = useMutation(
    {
      mutationFn: createCalendar,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["calendars"] });
        toast.success("Calendar created successfully");
        onSuccess?.();
        if (!onSuccess) {
          navigate({ to: "/calendars" });
        }
      },
      onError: (error) => {
        toast.error(error.message || "Failed to create calendar");
      },
    },
  );

  const { mutate: updateCalendarMutation, isPending: isUpdating } = useMutation(
    {
      mutationFn: updateCalendar,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["calendars"] });
        if (calendar?.id) {
          queryClient.invalidateQueries({
            queryKey: ["calendar", calendar.id],
          });
        }
        toast.success("Calendar updated successfully");
        onSuccess?.();
        if (!onSuccess) {
          navigate({ to: "/calendars" });
        }
      },
      onError: (error) => {
        toast.error(error.message || "Failed to update calendar");
      },
    },
  );

  const onSubmit = (data: CalendarFormData) => {
    if (isEdit && calendar?.id) {
      updateCalendarMutation({ id: calendar.id, name: data.name });
    } else {
      createCalendarMutation(data);
    }
  };

  const isSubmitting = isCreating || isUpdating;

  const formContent = (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Calendar Name</FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter calendar name"
                  {...field}
                  disabled={isSubmitting}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex gap-4">
          {!onSuccess && (
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate({ to: "/calendars" })}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
          )}
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting
              ? "Saving..."
              : isEdit
                ? "Update Calendar"
                : "Create Calendar"}
          </Button>
        </div>
      </form>
    </Form>
  );

  if (!showCard) {
    return formContent;
  }

  return (
    <Card className="w-full max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle>{isEdit ? "Edit Calendar" : "Create Calendar"}</CardTitle>
        <CardDescription>
          {isEdit
            ? "Update calendar information"
            : "Create a new calendar for organizing events"}
        </CardDescription>
      </CardHeader>
      <CardContent>{formContent}</CardContent>
    </Card>
  );
}
